import { useEffect, useRef, useState } from 'react'
import { ErroreApi } from '../servizi/api.ts'
import { useCatalogo } from '../servizi/sessione.tsx'
import { caricaDocumento } from '../servizi/documenti.ts'
import { attesaAntivirus } from '../servizi/regole-documenti.ts'
import {
  LATO_LUNGO_MASSIMO, MIME_FOTO, QUALITA_JPEG, dimensioniRidotte, nomeFoto, nomePdf, pesoLeggibile,
} from '../servizi/regole-foto.ts'
import { Bottone, Scelta } from './base.tsx'
import { Avviso } from './Pezzi.tsx'
import { IconaImmagine } from './Icone.tsx'

/** Foto ridotta pronta per il caricamento, con le misure prima e dopo la riduzione. */
export type FotoRidotta = {
  dati: Blob
  larghezza: number
  altezza: number
  originale: { larghezza: number; altezza: number; dimensione: number }
}

/**
 * Riduzione della foto nel browser (D-03): lato lungo a 1600 px, JPEG con qualita' 0,7.
 *
 * Il file si decodifica con un elemento <img> e si ridisegna su una tela (canvas) delle misure
 * ridotte. <img> e drawImage applicano l'orientamento EXIF (`image-orientation: from-image`,
 * predefinito da Chrome 81, Firefox 77 e Safari 13.1): una foto scattata con il telefono in
 * verticale esce dritta anche dopo che il dato di orientamento e' stato tolto.
 *
 * Metadati. Il file della fotocamera porta i metadati EXIF: modello del telefono, data e ora dello
 * scatto e, se la localizzazione e' attiva, la posizione GPS. La tela contiene solo pixel, e toBlob
 * codifica un JPEG nuovo: intestazione JFIF (APP0) e profilo colore sRGB (APP2 ICC), che non
 * descrive lo scatto, e nessun segmento APP1 Exif, quindi nessuna posizione, nessun modello, nessuna
 * data di scatto. Verificato il 15/09/2026 su Chromium 152 con questa stessa funzione: un JPEG
 * 4000 x 3000 con segmento APP1 Exif (Orientation 6 e IFD GPS con GPSLatitudeRef, GPSLatitude,
 * GPSLongitudeRef, GPSLongitude) esce in JPEG 1200 x 1600 con i soli segmenti APP0 JFIF e APP2 ICC
 * prima dei dati, senza l'identificativo «Exif» e senza il tag GPSInfo in alcun punto del file; il
 * riquadro in alto a sinistra del file compare in alto a destra, quindi l'orientamento e' applicato.
 * Un JPEG 1000 x 700 con lo stesso segmento esce 1000 x 700, senza Exif. Su Safari e Firefox vale lo
 * stesso per costruzione, perche' la tela non ha metadati da scrivere; il collaudo sui telefoni
 * resta da fare (REQ-121).
 *
 * Il fondo bianco sotto il disegno serve alle immagini con trasparenza: il JPEG non ha canale alfa
 * e i pixel trasparenti uscirebbero neri. E' contenuto dell'immagine, non un colore dell'interfaccia.
 *
 * Un formato che il browser non decodifica (per esempio HEIC su un computer Windows) non si riduce
 * e non si carica: caricare il file originale conserverebbe la posizione GPS.
 */
export async function riduciFoto(file: Blob): Promise<FotoRidotta> {
  const indirizzo = URL.createObjectURL(file)
  try {
    const immagine = new Image()
    immagine.decoding = 'async'
    immagine.src = indirizzo
    await immagine.decode()
    const ridotte = dimensioniRidotte(immagine.naturalWidth, immagine.naturalHeight)
    const tela = document.createElement('canvas')
    tela.width = ridotte.larghezza
    tela.height = ridotte.altezza
    const contesto = tela.getContext('2d')
    if (!contesto) throw new Error('tela non disponibile')
    contesto.fillStyle = '#ffffff'
    contesto.fillRect(0, 0, ridotte.larghezza, ridotte.altezza)
    contesto.imageSmoothingEnabled = true
    contesto.imageSmoothingQuality = 'high'
    contesto.drawImage(immagine, 0, 0, ridotte.larghezza, ridotte.altezza)
    const dati = await new Promise<Blob | null>((risolvi) => tela.toBlob(risolvi, MIME_FOTO, QUALITA_JPEG))
    // Un browser che non codifica JPEG ripiega su PNG: in quel caso la foto non rispetta D-03.
    if (!dati || dati.type !== MIME_FOTO) throw new Error('codifica JPEG non disponibile')
    return {
      dati, ...ridotte,
      originale: { larghezza: immagine.naturalWidth, altezza: immagine.naturalHeight, dimensione: file.size },
    }
  } finally {
    URL.revokeObjectURL(indirizzo)
  }
}

type Fase = 'scelta' | 'elaborazione' | 'anteprima' | 'invio'

/**
 * «Fotografa il documento» (D-03): scatto, riduzione, anteprima, tipo di documento, caricamento con
 * lo stesso percorso cifrato e la stessa coda antivirus degli altri documenti (servizi/documenti.ts).
 *
 * Riusabile: si usa nella linguetta Documenti accanto a «Scegli un file» e come passo finale,
 * saltabile, della creazione della scheda. Sui telefoni `capture="environment"` apre la fotocamera
 * posteriore; sul computer si sceglie un'immagine. Il campo file resta nascosto e lo apre un
 * bottone: un <label> non si raggiunge con il tabulatore.
 *
 * `caricata` si chiama dopo ogni conferma: si possono caricare piu' foto (fronte e retro).
 *
 * `fileIniziale`: il documento letto con «Compila dal documento» (lettura con l'AI), da allegare alla
 * scheda appena salvata. Si apre subito in anteprima: una foto si riduce come le altre, un PDF si
 * carica com'e', con lo stesso percorso cifrato e la stessa coda antivirus.
 */
export function FotoDocumento({ personaId, tipoIniziale = '', caricata, fuocoIniziale = false, fileIniziale = null }: {
  personaId: string
  /** Tipo di documento proposto, per esempio quello registrato sulla scheda. */
  tipoIniziale?: string | null
  caricata?: () => void
  /** Fuoco sul comando al primo disegno: per il passo della creazione, che compare al posto del modulo. */
  fuocoIniziale?: boolean
  /** File gia' scelto, da proporre subito: foto o PDF. */
  fileIniziale?: File | null
}) {
  const tipi = useCatalogo('tipo_documento')
  const radice = useRef<HTMLDivElement>(null)
  const comando = useRef<HTMLDivElement>(null)
  const ingresso = useRef<HTMLInputElement>(null)
  const [fase, setFase] = useState<Fase>('scelta')
  const [foto, setFoto] = useState<FotoRidotta | null>(null)
  const [pdf, setPdf] = useState<File | null>(null)
  const [anteprima, setAnteprima] = useState<string | null>(null)
  const [tipo, setTipo] = useState('')
  const [erroreTipo, setErroreTipo] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [richiestaFuoco, setRichiestaFuoco] = useState(0)

  // Il tipo proposto vale solo se e' una voce attiva del catalogo: un valore storico non si offre.
  const tipoProposto = tipi.voci.some((v) => v.codice === tipoIniziale) ? (tipoIniziale ?? '') : ''

  const fuocoSulComando = () => comando.current?.querySelector<HTMLButtonElement>('button')?.focus()

  useEffect(() => {
    if (fuocoIniziale && !fileIniziale) fuocoSulComando()
  }, [fuocoIniziale, fileIniziale])

  // Il documento letto si propone una volta sola, appena il passo compare.
  useEffect(() => {
    if (!fileIniziale) return
    if (fileIniziale.type === 'application/pdf' || /\.pdf$/i.test(fileIniziale.name)) {
      setPdf(fileIniziale)
      setTipo((t) => t || tipoProposto)
      setFase('anteprima')
    } else {
      void scattata(fileIniziale)
    }
  }, [fileIniziale])

  // Chiusa l'anteprima (foto caricata, «Annulla», foto non leggibile dopo «Scatta di nuovo») il
  // bottone che aveva il fuoco esce dal DOM e il fuoco finirebbe sul corpo della pagina: chi usa la
  // tastiera ripartirebbe dall'inizio. Il fuoco si sposta dopo il disegno, non nel gestore: li'
  // React non ha ancora aggiornato il DOM, il comando non esiste e al suo posto c'e' «Carica la foto»,
  // disabilitato durante l'invio. Un contatore, e non la fase, fa girare l'effetto a ogni richiesta.
  useEffect(() => {
    if (richiestaFuoco > 0) fuocoSulComando()
  }, [richiestaFuoco])

  // L'indirizzo dell'anteprima tiene in memoria la foto: si rilascia quando cambia o si esce.
  useEffect(() => () => { if (anteprima) URL.revokeObjectURL(anteprima) }, [anteprima])

  // Dopo lo scatto il fuoco va sul tipo di documento, l'unica scelta che resta da fare.
  useEffect(() => {
    if (fase === 'anteprima') radice.current?.querySelector<HTMLSelectElement>('select')?.focus()
  }, [fase])

  const apriFotocamera = () => ingresso.current?.click()

  function chiudiAnteprima() {
    setFoto(null); setPdf(null); setAnteprima(null); setErroreTipo(null); setFase('scelta')
    setRichiestaFuoco((n) => n + 1)
  }

  async function scattata(file: File | undefined) {
    if (!file) return
    setErrore(null); setEsito(null); setPdf(null); setFase('elaborazione')
    try {
      const ridotta = await riduciFoto(file)
      setFoto(ridotta)
      setAnteprima(URL.createObjectURL(ridotta.dati))
      setTipo((t) => t || tipoProposto)
      setFase('anteprima')
    } catch {
      chiudiAnteprima()
      setErrore('La foto non si può leggere in questo browser, quindi non si riduce e non si carica. '
        + 'Riprovare con la fotocamera oppure con un’immagine JPEG o PNG.')
    }
  }

  async function carica() {
    if (!foto && !pdf) return
    if (!tipo) {
      setErroreTipo('Scegliere il tipo di documento.')
      radice.current?.querySelector<HTMLSelectElement>('select')?.focus()
      return
    }
    setErrore(null); setErroreTipo(null); setFase('invio')
    try {
      if (pdf) await caricaDocumento(personaId, { nome: nomePdf(tipo), tipo, dati: pdf, mime: 'application/pdf' })
      else if (foto) await caricaDocumento(personaId, { nome: nomeFoto(tipo), tipo, dati: foto.dati, mime: MIME_FOTO })
      const nome = tipi.nomeDi(tipo)
      const cosa = pdf ? `PDF caricato come «${nome}» e salvato` : `Foto caricata come «${nome}» e salvata`
      chiudiAnteprima()
      setTipo('')
      // La foto e' salvata subito; si scarica dopo la verifica antivirus (osservazioni n. 4, punto 13).
      setEsito(`${cosa}. ${attesaAntivirus(new Date()).verifica}`)
      caricata?.()
    } catch (e) {
      setFase('anteprima')
      setErrore(e instanceof ErroreApi ? e.message : `Caricamento non riuscito: ${(e as Error).message}`)
    }
  }

  const aperta = fase === 'anteprima' || fase === 'invio' || !!errore || !!esito

  return (
    <div ref={radice} className={`persona-foto${aperta ? ' persona-foto-aperta' : ''}`}>
      <input ref={ingresso} type="file" accept="image/*" capture="environment" hidden
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void scattata(f) }} />

      {(fase === 'scelta' || fase === 'elaborazione') && (
        <div ref={comando}>
          <Bottone variante="secondario" icona={<IconaImmagine />} inCorso={fase === 'elaborazione'}
                   onClick={apriFotocamera}>
            {fase === 'elaborazione' ? 'Preparazione della foto…' : 'Fotografa il documento'}
          </Bottone>
        </div>
      )}

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {/* La regione di stato resta nel documento anche vuota: una regione creata insieme al suo
          testo non viene annunciata da tutti i lettori di schermo. */}
      <div role="status">{esito && <Avviso tipo="successo">{esito}</Avviso>}</div>

      {((foto && anteprima) || pdf) && (fase === 'anteprima' || fase === 'invio') && (
        <>
          {pdf ? (
            <p className="piccolo tenue">
              PDF del documento letto, {pesoLeggibile(pdf.size)}: si carica così com’è.
            </p>
          ) : foto && anteprima && (
            <figure className="persona-foto-anteprima">
              <img src={anteprima} alt="Anteprima della foto del documento" />
              <figcaption className="piccolo tenue">
                {foto.larghezza} × {foto.altezza} px, {pesoLeggibile(foto.dati.size)} in JPEG
                {' '}(scattata {foto.originale.larghezza} × {foto.originale.altezza} px,
                {' '}{pesoLeggibile(foto.originale.dimensione)}). Lato lungo al massimo {LATO_LUNGO_MASSIMO} px,
                senza posizione né altri dati dello scatto.
              </figcaption>
            </figure>
          )}
          <div className="persona-foto-tipo">
            <Scelta etichetta="Tipo di documento" obbligatorio valore={tipo} vuota="Scegliere il tipo"
                    errore={erroreTipo} disabilitato={fase === 'invio'}
                    cambia={(v) => { setTipo(v); setErroreTipo(null) }}
                    opzioni={tipi.voci.map((v) => ({ valore: v.codice, etichetta: v.nome }))} />
          </div>
          <div className="persona-foto-comandi">
            <Bottone variante="primario" inCorso={fase === 'invio'} onClick={() => void carica()}>
              {pdf ? 'Carica il PDF' : 'Carica la foto'}
            </Bottone>
            <Bottone variante="secondario" disabled={fase === 'invio'} onClick={apriFotocamera}>
              Scatta di nuovo
            </Bottone>
            <Bottone variante="discreto" disabled={fase === 'invio'} onClick={chiudiAnteprima}>
              Annulla
            </Bottone>
          </div>
        </>
      )}
    </div>
  )
}
